// @ts-check
// Página del editor visual de diagramas (TSK-0027, docs/03-frontend.md
// §4.3 "pages": "Compone features y componentes para una URL/vista.
// Gestiona montaje y desmontaje. No implementa cliente HTTP ni reglas
// reutilizables"). Este módulo monta la única feature `diagram-editor`
// (`../features/diagram-editor/index.js`) en el punto de anclaje que ya
// reserva el shell de TSK-0009 (`#diagram-editor-root`, dentro de
// `<main id="main-content">`, ver `frontend/index.html`), gestiona el foco
// al montar y garantiza un desmontaje limpio (sin listeners duplicados si
// se monta más de una vez).
//
// TSK-0054 (Ola 11, CAP-EDITOR-EXERCISE-LOAD): el editor deja de ser
// standalone. Esta página identifica QUÉ ejercicio mostrar leyendo el
// parámetro de query string `?ejercicio=<slug>` de la URL actual (decisión
// de diseño: una URL con query string es el mecanismo más simple y
// enlazable -- sin routing propio ni estado global -- para que un enlace
// externo, p.ej. desde el futuro catálogo de MOD-EXERCISE-BANK, abra el
// editor ya apuntando a un ejercicio concreto). Si el parámetro está
// presente, se pide su detalle público a `exercise-client.js`
// (`GET /api/v1/exercises/{slug}` vía `apiRequest`, nunca `fetch` directo) y
// se muestra su título/descripción reales alrededor del editor ya
// existente. Esta página NO importa nada de dentro de
// `frontend/src/features/diagram-editor/**` salvo su `index.js` público, y
// no modifica su lógica interna: solo añade una sección de enunciado junto
// al editor ya montado.
//
// Si no hay parámetro `ejercicio`, la sección de enunciado permanece oculta
// (`hidden`) y el editor funciona exactamente igual que antes (nunca se
// rompe el uso standalone). Si la petición falla (404, red, timeout), se
// anuncia un mensaje de error accesible (`role="alert"`) en vez de dejar
// una pantalla en blanco, y el editor sigue siendo utilizable sin
// enunciado.
//
// TSK-0085 (CAP-IDENTITY-AUTH-FLOW, última pieza de la tanda de rediseño +
// autenticación): esta página deja de ser accesible sin sesión. El punto de
// entrada real ya NO es `montarPaginaEditor()` directamente, sino
// `arrancarPaginaEditor()` (ver más abajo), que:
//   1. Comprueba la sesión con `state/session-store.js#cargarSesionActual()`
//      (TSK-0082, ya "done") ANTES de tocar nada del editor. Si no hay
//      sesión (o si la comprobación falla por cualquier otro motivo, ver
//      decisión documentada en su propio docstring), redirige a
//      `login.html` SIN llamar a `montarPaginaEditor` en absoluto: ni el
//      canvas, ni la paleta, ni el enunciado de ningún ejercicio llegan a
//      construirse antes de la redirección.
//   2. Si hay sesión, monta el editor normalmente (`montarPaginaEditor`,
//      sin cambios de comportamiento en sí misma) pasándole el `usuario`
//      real (`state/session-store.js#obtenerUsuario()`, una copia, nunca la
//      referencia interna del store) y un `onCerrarSesion` que orquesta el
//      logout (ver `manejarCerrarSesion`).
//
// Decisión registrada explícitamente sobre el parámetro de retorno de
// login (CLAUDE.md, "no decidas silenciosamente"): `login-page.js`
// (TSK-0083, ya "done", fuera de las rutas permitidas de esta tarea) NO
// soporta ningún parámetro `?next=`/equivalente -- su `RUTA_TRAS_LOGIN` es
// una constante fija y su formulario no lee ningún query string (verificado
// leyendo el archivo real antes de asumir el mockup al pie de la letra, tal
// como pedía esta ficha). Por tanto esta página redirige a `login.html` sin
// ningún parámetro: retomar el editor exactamente donde se dejó tras un
// login posterior queda fuera de alcance hasta que una tarea futura amplíe
// `login-page.js` para soportarlo.
//
// `editor-controller.js` (TSK-0027..TSK-0080) NO tenía ninguna barra
// superior/pastilla de usuario lista antes de esta tarea (verificado
// leyendo el archivo real, tal como pedía esta ficha): esa estructura se
// construye en ese mismo archivo (está en las rutas permitidas de esta
// tarea) como una cabecera OPCIONAL (ver su "DECISIÓN 9"), nunca importando
// `state/session-store.js` desde dentro de la feature -- esta página sigue
// siendo la ÚNICA que orquesta sesión (docs/03-frontend.md: "components no
// llaman fetch; services no tocan el DOM").

// TSK-0117 (CAP-PROGRESS-EXERCISE-SELECTOR): esta página monta también el
// selector de ejercicios del bloque 'diagrama' (`components/
// exercise-selector.js`, TSK-0116, componente puro que NO carga datos por sí
// mismo) por encima del dashboard de enunciado/puntuación. Esta página es la
// única responsable de cargar los datos (`services/progress-client.js#
// obtenerEjerciciosDeBloque`, TSK-0115) y de reaccionar a la selección --
// exactamente el reparto de capas de docs/03-frontend.md §4 ("components no
// llaman fetch; services no tocan el DOM"). Seleccionar un ejercicio
// desbloqueado/completado navega a `index.html?ejercicio=<slug>`
// (`navegarAEjercicio`, más abajo), reutilizando tal cual el mecanismo de
// TSK-0054 ya documentado más abajo: esta misma página ya sabe leer ese
// parámetro al montarse, así que no se introduce ningún routing/estado
// nuevo. Tanto la carga (`opciones.cargarEjerciciosDeBloque`) como la
// navegación (`opciones.navegarAEjercicio`) son inyectables en
// `montarPaginaEditor`/`arrancarPaginaEditor`, mismo patrón ya usado por
// `opciones.redirigirALogin` (TSK-0085): jsdom no implementa navegación de
// documento completo y los tests no deben depender de red real.

// TSK-0124 (CAP-STRUCT-CORRECTION-OUTPUT-DIAGRAM, DEC-041/DEC-051): esta
// página es quien resuelve `ejercicioId`/`csrfToken` para el nuevo panel
// "Autocorregir" del editor (`features/diagram-editor/autocorrection-
// panel.js`), exactamente el mismo reparto de capas ya aprobado para el
// interruptor de tema (DEC-044/TSK-0108, ver `pages/shell-nav.js`:
// "El componente... es PURO... la persistencia en backend vive aquí, en la
// página, invocando el service... nunca desde el componente"):
//   - `csrfToken`: `state/session-store.js#obtenerCsrfToken()` (ya
//     comprobada la sesión por `arrancarPaginaEditor` antes de montar nada,
//     TSK-0085), se pasa a `crearEditorDiagramas` tal cual la construye
//     `montarPaginaEditor` -- síncrono, disponible en el mismo instante en
//     que se monta el editor.
//   - `ejercicioId`: el UUID real del ejercicio (`EjercicioPublico.id`,
//     `exercise-client.js`) solo se conoce cuando la petición ya existente
//     de TSK-0054 (`obtenerEjercicioPorSlug`) resuelve -- asíncrona,
//     lanzada en paralelo al montaje del editor (ver más abajo). Por eso se
//     entrega con un SETTER (`instancia.establecerEjercicioActual`, nuevo
//     método público de `crearEditorDiagramas`/`editor-controller.js`) en
//     vez de como una opción de construcción: no hay forma de tenerlo listo
//     a tiempo para el `crearEditorDiagramas({...})` inicial sin bloquear el
//     montaje del editor a esa petición de red (lo que rompería el criterio
//     ya aprobado de TSK-0054 de que el editor se muestra de inmediato,
//     "sin bloquear el montaje", con el enunciado llegando después).
//   Sin `?ejercicio=` en la URL (uso standalone), `establecerEjercicioActual`
//   nunca se llama: el panel de autocorrección se queda con `ejercicioId:
//   null` (deshabilitado, con un mensaje explícito), comportamiento ya
//   documentado en `autocorrection-panel.js` para ese caso.
import { crearBotonTraducir } from '../features/translation/translation-panel.js';
import { construirRutaLogin } from '../utils/retorno-tras-login.js';
import { abrirEditor, dejarEnEditor } from '../features/translation/entrega.js';
import { colocarEnRanuraTraducir } from '../utils/ranura-traducir.js';
import { CLAVE_ALMACENAMIENTO } from '../features/diagram-editor/editor-persistence.js';
import { ejercicioRecordado, recordarEjercicio } from '../state/ejercicio-seleccionado.js';
import { ambitoDeEjercicio, migrarBorradorHeredado } from '../state/ambito-borrador.js';
import { serializarPrograma } from '../features/diagram-editor/ir-serialization.js';
import {
  crearEditorDiagramas,
  MARCA_EDITOR_DIAGRAMAS,
  RUTA_GUIA_DIAGRAMAS,
} from '../features/diagram-editor/index.js';
import { crearCabeceraEditor } from '../components/editor-hud.js';
import { activarMomentos } from '../components/base/momentos.js';
import { obtenerEjercicioPorSlug } from '../services/exercise-client.js';
import { ApiError } from '../services/api-errors.js';
import { cargarSesionActual, obtenerCsrfToken } from '../state/session-store.js';
import { crearSelectorEjercicios } from '../components/exercise-selector.js';
import { crearSeccionEnunciado } from '../components/exercise-statement.js';
import { mensajeDeErrorEnunciado } from './exercise-statement-errors.js';
import { obtenerEjerciciosDeBloque, soloSePractica } from '../services/progress-client.js';
import { crearPedidorDeLista } from './pedir-lista-ejercicios.js';
import './diagram-editor-page.css';

/** Id del punto de anclaje reservado en `frontend/index.html`. */
export const ID_ANCLAJE = 'diagram-editor-root';

/** Nombre del parámetro de query string que identifica el ejercicio a cargar. */
export const PARAMETRO_EJERCICIO = 'ejercicio';

/**
 * Bloque fijo de esta página (TSK-0117, CAP-PROGRESS-EXERCISE-SELECTOR): el
 * editor de diagramas solo muestra el selector de ejercicios del bloque
 * 'diagrama' -- 'pseudocodigo'/'python' quedan para sus páginas hermanas
 * (pseudocode.html/python.html, contrato de TSK-0089), fuera de alcance
 * aquí.
 */
export const BLOQUE_EJERCICIOS = 'diagrama';


/**
 * Ruta a la que se redirige si no hay sesión (TSK-0085), y a la que también
 * redirige el botón "Cerrar sesión" tras completarse el logout. Sin
 * parámetro de retorno -- ver decisión documentada en la cabecera de este
 * archivo (`login-page.js` no lo soporta todavía).
 */
export const RUTA_LOGIN = 'login.html';

/** @type {ReturnType<typeof crearEditorDiagramas> | null} */
let instanciaActual = null;
/** @type {ReturnType<typeof crearBotonTraducir> | null} */
let botonTraducirActual = null;

/** @type {HTMLElement | null} */
let wrapperActual = null;

/** @type {AbortController | null} */
let controladorCargaActual = null;

/**
 * Manejador de `popstate` de la página montada. Estado de módulo, como el
 * resto: `desmontarPaginaEditor` tiene que poder retirarlo o quedaría un
 * listener global apuntando a una página ya destruida.
 * @type {((evento: Event) => void) | null}
 */
let manejadorPopstate = null;

/** @type {AbortController | null} */
let controladorEjerciciosActual = null;

/** @type {ReturnType<typeof crearSelectorEjercicios> | null} */
let selectorEjerciciosActual = null;


/**
 * Lee el slug del ejercicio a cargar desde `?ejercicio=<slug>` en la URL
 * actual del documento. Devuelve `null` si no hay `window`/`location`
 * disponible (entornos sin DOM) o si el parámetro no está presente o queda
 * vacío tras recortar espacios.
 * @returns {string | null}
 */
function obtenerSlugDeLaUrl() {
  if (typeof window === 'undefined' || !window.location) return null;
  const parametros = new URLSearchParams(window.location.search);
  const slug = parametros.get(PARAMETRO_EJERCICIO);
  if (typeof slug !== 'string') return null;
  const recortado = slug.trim();
  return recortado.length > 0 ? recortado : null;
}

/**
 * Textos con los que esta página traduce un fallo al cargar el enunciado
 * (la lógica de clasificación es compartida, ver
 * `pages/exercise-statement-errors.js`). Los tres insisten en que "el editor
 * sigue disponible sin enunciado": aquí el enunciado es un extra sobre un
 * editor que funciona solo, a diferencia de las otras dos páginas.
 * @type {import('./exercise-statement-errors.js').TextosDeErrorEnunciado}
 */
const TEXTOS_ERROR_ENUNCIADO = {
  noEncontrado:
    'No se ha encontrado el ejercicio solicitado. El editor sigue disponible sin enunciado.',
  sinConexion:
    'No se ha podido conectar con el servidor para cargar el enunciado. El editor sigue disponible sin enunciado.',
  generico: 'No se ha podido cargar el enunciado del ejercicio. El editor sigue disponible sin enunciado.',
};

/**
 * Traduce un error de carga de la LISTA de ejercicios del bloque a un
 * mensaje en español apto para la interfaz (nunca se expone el mensaje
 * técnico crudo, 03-frontend §9.1/§8.4). Caso especial documentado
 * (criterio de aceptación de esta tarea): `ApiError` con `kind ===
 * 'unauthorized'` (401 -- la sesión ha expirado entre que se montó esta
 * página y se lanzó esta petición, o cualquier otro motivo por el que el
 * backend ya no reconoce la cookie de sesión) recibe un mensaje específico
 * que invita a volver a iniciar sesión, en vez del genérico -- es
 * información accionable real para quien lo lee, no solo "algo falló".
 * Esta página NO redirige automáticamente a login.html en este caso (a
 * diferencia de `arrancarPaginaEditor`, que sí comprueba sesión antes de
 * montar nada): forzar una redirección completa de documento solo porque
 * ESTA petición concreta devolvió 401 sería una decisión demasiado agresiva
 * para un widget secundario del dashboard (el editor y el enunciado ya
 * cargado, si lo hay, siguen siendo utilizables) -- se limita a degradar de
 * forma accesible el selector, sin crashear ni dejar una promesa sin
 * manejar.
 * @param {unknown} error
 * @returns {string}
 */
function mensajeDeErrorEjercicios(error) {
  if (error instanceof ApiError && error.kind === 'unauthorized') {
    return 'Tu sesión ha expirado. Vuelve a iniciar sesión para ver tus ejercicios.';
  }
  return 'No se ha podido cargar la lista de ejercicios. Inténtalo de nuevo más tarde.';
}

/**
 * Monta la página del editor dentro de `contenedor` (por defecto, el
 * anclaje `#diagram-editor-root` del shell). Si ya había una instancia
 * montada sin desmontar (p.ej. una llamada anterior a `montarPaginaEditor`
 * de la que nadie llamó `desmontarPaginaEditor`), se desmonta primero: así
 * nunca quedan dos composiciones vivas a la vez ni listeners duplicados
 * (criterio de aceptación de esta tarea).
 *
 * Devuelve `null` sin lanzar si el anclaje no existe en el documento (p.ej.
 * este módulo se carga en un documento que todavía no tiene el shell de
 * `frontend/index.html`, o en un test que monta su propio contenedor y no
 * pasa ningún `contenedor` explícito): un fallo de anclaje no es un error
 * de programación de esta página, es una precondición del entorno.
 *
 * Si la URL actual incluye `?ejercicio=<slug>` (ver `obtenerSlugDeLaUrl`),
 * lanza en paralelo (sin bloquear el montaje del editor) la petición de su
 * detalle público y actualiza la sección de enunciado cuando resuelve o
 * falla.
 *
 * TSK-0085: esta función NO comprueba sesión (eso es responsabilidad de
 * `arrancarPaginaEditor`, el punto de entrada real de esta página, ver más
 * abajo) -- se mantiene deliberadamente como el punto de montaje "puro" que
 * ya usaban los tests existentes de esta página (`tests/accessibility/
 * pages/diagram-editor-exercise-load.spec.js`, TSK-0054, fuera de las rutas
 * permitidas de esta tarea): pasarle un `contenedor` explícito sigue
 * montando el editor sin más precondiciones, exactamente igual que antes de
 * TSK-0085, para no romper esa suite ya aprobada que verifica composición
 * DOM (enunciado/error/axe), no el perímetro de seguridad. El perímetro de
 * seguridad real de la app vive en `arrancarPaginaEditor` (el único punto
 * de entrada que el auto-montaje real de esta página usa, ver el bloque IIFE
 * al final del archivo).
 *
 * TSK-0117 (CAP-PROGRESS-EXERCISE-SELECTOR): además monta el selector de
 * ejercicios del bloque `BLOQUE_EJERCICIOS` ('diagrama') por encima del
 * dashboard de enunciado/puntuación, con su propia carga fire-and-forget
 * (totalmente capturada -- nunca una promesa rechazada sin manejar) vía
 * `opciones.cargarEjerciciosDeBloque` (por defecto `obtenerEjerciciosDeBloque`
 * de `services/progress-client.js`). Seleccionar un ejercicio
 * desbloqueado/completado navega con `opciones.navegarAEjercicio` (por
 * defecto `navegarAEjercicio`, ver más abajo) a `index.html?ejercicio=<slug>`,
 * reutilizando el mecanismo de query string ya documentado arriba (TSK-0054)
 * en vez de inventar uno nuevo: esta MISMA página ya sabe leer ese parámetro
 * al montarse, así que no hace falta ningún routing/estado adicional --
 * "seleccionar un ejercicio" y "abrir el editor con `?ejercicio=`" son, a
 * propósito, el mismo mecanismo.
 * La pastilla de usuario + logout ya no se montan aquí: viven en el top-nav
 * superior (`pages/shell-nav.js` + `components/session-menu.js`), unificado en
 * todas las landing. Esta página solo monta el editor (con su marca) y aplica
 * la guarda de sesión en `arrancarPaginaEditor`.
 *
 * @param {HTMLElement} [contenedor]
 * @param {{
 *   cargarEjerciciosDeBloque?: typeof obtenerEjerciciosDeBloque,
 *   listaInicial?: readonly unknown[] | null,
 *   navegarAEjercicio?: (slug: string) => void,
 * }} [opciones] TSK-0117: `cargarEjerciciosDeBloque`/`navegarAEjercicio`
 *   inyectables (mismo patrón que `opciones.redirigirALogin` en
 *   `arrancarPaginaEditor`) para que los tests inyecten dobles
 *   deterministas -- jsdom no implementa navegación de documento completo
 *   y esta suite no debe depender de red real.
 * @returns {ReturnType<typeof crearEditorDiagramas> | null}
 */
export function montarPaginaEditor(contenedor, opciones = {}) {
  const raiz = contenedor ?? (typeof document === 'undefined' ? null : document.getElementById(ID_ANCLAJE));
  if (!raiz) return null;

  if (instanciaActual) {
    desmontarPaginaEditor();
  }

  // Ámbito del borrador: cada ejercicio tiene el suyo desde 2026-08-06 (ver
  // `state/ambito-borrador.js`). Antes había UN solo diagrama por cuenta, así
  // que traducir desde Python machacaba el del ejercicio en curso y los cinco
  // ejercicios compartían lienzo. Se resuelve de forma SÍNCRONA -- URL
  // primero, último recordado después -- porque el editor carga su diagrama
  // al construirse, unas líneas más abajo.
  const ambitoBorrador = ambitoDeEjercicio(obtenerSlugDeLaUrl() ?? ejercicioRecordado('diagrama'));
  // Adopta, una sola vez, el borrador único que quedó del esquema anterior.
  // Va al ejercicio que el alumno tuviera abierto (decisión del usuario).
  migrarBorradorHeredado(CLAVE_ALMACENAMIENTO, ambitoBorrador);

  // La lista ya recibida (`listaInicial`, al remontar por un cambio de
  // ejercicio) se usa una sola vez; las recargas van al servidor. Ver
  // `pedir-lista-ejercicios.js`.
  const cargarEjercicios = crearPedidorDeLista(
    opciones.cargarEjerciciosDeBloque ?? obtenerEjerciciosDeBloque,
    opciones.listaInicial,
  );
  // Elegir ejercicio ya NO recarga la página: se actualiza la URL con
  // `pushState` y se sustituye solo el enunciado (`cargarEjercicio`, más
  // abajo). El editor expone `establecerEjercicioActual`, así que el cambio
  // en caliente no necesita tocar la zona protegida.
  //
  // `navegarAEjercicio` sigue exportada e inyectable: los tests la sustituyen
  // para comprobar el destino, y jsdom no implementa una navegación de
  // documento completa.
  /** Lista de ejercicios ya recibida, para reutilizarla al remontar. */
  let ejerciciosCargados = /** @type {any[] | null} */ (null);

  const navegar =
    opciones.navegarAEjercicio ??
    ((slug) => {
      actualizarUrlEjercicio(slug);

      // Cada ejercicio tiene su propio diagrama guardado, y el editor lo carga
      // al CONSTRUIRSE (`ambitoBorrador`, ver `state/ambito-borrador.js`).
      // Cambiar de ejercicio remonta la página -- no recarga el documento, así
      // que no vuelve a descargar HTML ni JavaScript, y la lista de ejercicios
      // ya cargada se reutiliza para no pedirla de nuevo.
      //
      // Se aplaza a una microtarea para no destruir el selector desde dentro
      // de su propio manejador, y se comprueba que la página sigue siendo la
      // misma: un remontaje aplazado nunca debe devolver a la vida una página
      // que el llamador ya desmontó.
      if (ambitoDeEjercicio(slug) !== ambitoBorrador) {
        const listaEnMemoria = ejerciciosCargados;
        const instanciaQueRemonta = instanciaActual;
        globalThis.queueMicrotask(() => {
          if (instanciaActual !== instanciaQueRemonta) return;
          montarPaginaEditor(raiz, {
            ...opciones,
            listaInicial: listaEnMemoria,
          });
        });
        return;
      }

      cargarEjercicio(slug);
    });

  // Rediseño (Fase 4b): el enunciado es una franja plegable (`<details
  // open>`, como en los otros dos editores) para ganar alto de lienzo, con la
  // dificultad y la categoría en dos chips. Los textos no cambian.
  const seccionEnunciado = crearSeccionEnunciado({
    prefijoClase: 'exercise-statement',
    conMeta: true,
    colapsable: true,
    metaEnChips: true,
    claseRaiz: 'ui-panel',
  });
  // Rediseño (Fase 4b): el HUD de tres paneles. «Ejercicios» (con la marca
  // del editor y su guía como cabecera) y «Progreso» (el contador «X/N
  // desbloqueados», sacado del selector, con su barra de N segmentos) los
  // pone la página; el tercero, «Autocorregir ejercicio», es del editor.
  const selectorEjercicios = crearSelectorEjercicios({
    tituloSeccion: 'Ejercicios de diagramas',
    onSeleccionar: (slug) => navegar(slug),
    progresoAparte: true,
    // La marca y la guía del editor, en vez de su barra aparte
    // (`sinCabecera`, más abajo): mismos texto y destino.
    cabecera: crearCabeceraEditor({
      marca: MARCA_EDITOR_DIAGRAMAS,
      rutaGuia: RUTA_GUIA_DIAGRAMAS,
    }),
  });
  const hud = document.createElement('div');
  hud.className = 'diagram-editor-hud';
  hud.append(selectorEjercicios.elemento);
  if (selectorEjercicios.progreso) hud.append(selectorEjercicios.progreso);

  // Dashboard de ejecución (petición directa del usuario): enunciado +
  // puntuación agrupados como un único bloque visual, en vez de dos
  // secciones sueltas sin relación aparente entre sí (ver
  // `diagram-editor-page.css`, `.diagram-editor-dashboard`). Construido ANTES
  // de `crearEditorDiagramas` (a diferencia de la versión previa a esta
  // tarea) porque ahora se reenvía como dato OPACO (`dashboardElemento`,
  // "DECISIÓN 12" en `editor-controller.js`) en vez de añadirse directamente
  // a `wrapper`: petición directa del usuario de que quede DEBAJO de
  // "Ejercicios de diagramas" en vez de por encima de todo el editor
  // (topbar/selector incluidos), que es donde quedaba cuando era el primer
  // hijo de `wrapper`.
  const dashboard = document.createElement('div');
  dashboard.className = 'diagram-editor-dashboard';
  dashboard.append(seccionEnunciado.elemento);


  const instancia = crearEditorDiagramas({
    // El diagrama que se carga es el de ESTE ejercicio, no el único que había
    // antes para toda la cuenta (ver `state/ambito-borrador.js`).
    ambitoBorrador,
    // Al acertar, la lista de ejercicios que hay en pantalla se queda
    // desfasada: el siguiente acaba de desbloquearse. Antes había que
    // refrescar la página a mano para verlo.
    onEjercicioCompletado: () => recargarListaDeEjercicios(),
    // La pastilla de usuario + botón "Cerrar sesión" YA NO viven en la topbar
    // del editor: se han unificado en el top-nav superior (`components/
    // session-menu.js`, montado por `pages/shell-nav.js`), presente en todas
    // las landing. Se pasa `usuario: null` a propósito: `crearTopbar` conserva
    // la marca ("Flujo · Editor de diagramas") pero sin pastilla/logout. La
    // guarda de sesión sigue viviendo en `arrancarPaginaEditor`.
    usuario: null,
    onCerrarSesion: null,
    // Rediseño (Fase 4b): la marca y la guía van en el panel Ejercicios del
    // HUD (`crearCabeceraEditor`), no en una barra aparte.
    sinCabecera: true,
    // TSK-0151 (DEC-058 punto 4): el selector deja de montarse en una
    // sección aparte del `wrapper` (ver más abajo) y se reenvía como dato
    // OPACO al controlador, que lo inserta como hermano de la topbar.
    // La orquestación (esta misma función + `mostrarCargando`/fetch/
    // `mostrarLista`/`mostrarError`/`destruir`, más abajo) no cambia.
    // Rediseño (Fase 4b): los paneles «Ejercicios» y «Progreso» del HUD.
    selectorEjerciciosElemento: hud,
    // "DECISIÓN 12" en `editor-controller.js`: mismo mecanismo que el
    // selector -- el controlador lo inserta justo después de él (o de la
    // topbar), antes del propio título/layout del editor.
    dashboardElemento: dashboard,
    // TSK-0124: ver la nota de cabecera de este archivo ("De dónde salen
    // ejercicioId/csrfToken"). `obtenerCsrfToken()` devuelve `null` si no
    // hay sesión cargada en memoria (p.ej. tests standalone que montan esta
    // página sin pasar por `arrancarPaginaEditor`) -- se normaliza a `''`,
    // que `autocorrection-panel.js` trata como "sin token" (botón
    // deshabilitado/mensaje de error explícito, nunca una llamada de red
    // con un token vacío).
    csrfToken: obtenerCsrfToken() ?? '',
  });

  const wrapper = document.createElement('div');
  wrapper.className = 'diagram-editor-page';
  // TSK-0151 (DEC-058 punto 4) + revisión "DECISIÓN 12" (2026-07-21): ni el
  // selector de ejercicios ni el dashboard de enunciado/puntuación se añaden
  // aquí -- ambos se reenvían como datos OPACOS a `crearEditorDiagramas` (ver
  // arriba), que los inserta dentro de su propia raíz, en ese orden, debajo
  // de la topbar. Sus nodos DOM (`selectorEjercicios.elemento`/`dashboard`)
  // siguen existiendo igual (esta página sigue orquestando su ciclo de vida
  // completo, ver `selectorEjerciciosActual`/`destruir()` más abajo): solo
  // cambia QUIÉN decide dónde viven en el árbol.

  // Botón "Traducir a…" (DEC-107). Se monta desde la PÁGINA y no desde la
  // feature del editor: así los tres editores comparten exactamente el mismo
  // componente sin que ninguno de ellos tenga que conocer a los otros dos, y
  // el editor de diagramas -- zona protegida -- no se toca por dentro.
  const botonTraducir = crearBotonTraducir({
    desde: 'diagrama',
    // `obtenerPrograma()` devuelve el MODELO del editor (nodos con `id` y
    // campos propios de la edición). Se serializa a IR-JSON puro antes de
    // traducir, que es el contrato con el que trabajan los traductores.
    obtenerFuente: () => serializarPrograma(instancia.obtenerPrograma()),
    entregar: (hacia, resultado) => {
      if (!dejarEnEditor(hacia, resultado)) return;
      abrirEditor(hacia);
    },
  });
  botonTraducirActual = botonTraducir;
  const barraTraduccion = document.createElement('div');
  barraTraduccion.className = 'traduccion-barra';
  barraTraduccion.append(botonTraducir.elemento);
  // Y de ahí, a su sitio de verdad: la fila del botón "Ejecutar" de la
  // consola de este editor. `barraTraduccion` queda como respaldo -- si esa
  // ranura no existe (una composición sin consola, o un test que monta la
  // página aislada), el botón se queda aquí y sigue funcionando.
  colocarEnRanuraTraducir(botonTraducir.elemento);
  wrapper.append(instancia.elemento);
  wrapper.append(barraTraduccion);

  raiz.replaceChildren(wrapper);
  wrapperActual = wrapper;
  instanciaActual = instancia;
  selectorEjerciciosActual = selectorEjercicios;

  // Gestión de foco al montar (criterio de aceptación de esta tarea): el
  // editor sigue siendo el contenido principal de esta vista; se lleva el
  // foco a su encabezado igual que antes de TSK-0054, con independencia de
  // si hay o no enunciado (el enunciado se anuncia mediante `role=status`/
  // `role=alert`, no mediante robo de foco -- WAI-ARIA APG desaconseja mover
  // el foco a contenido que aparece de forma asíncrona sin interacción
  // directa del usuario). `enfocarInicial` ya se encarga de fijar
  // `tabindex="-1"` sin dejar nunca un `tabindex` positivo.
  instancia.enfocarInicial();

  /** Ejercicio que se está trabajando ahora mismo. Cambia sin recargar. */
  let slugActual = /** @type {string | null} */ (null);

  /**
   * Carga el enunciado de `slug` y se lo entrega al editor.
   *
   * Extraída a función el 2026-08-05 para poder invocarla también al CAMBIAR
   * de ejercicio, no solo al montar. Antes, elegir un ejercicio hacía
   * `window.location.assign(...)`: una navegación de documento completa que
   * volvía a descargar HTML y JavaScript y reconstruía el editor desde cero.
   * En el plan gratuito de Render, con la API dormida, eso se notaba mucho.
   *
   * Cancela la carga anterior si seguía en vuelo: sin esto, elegir dos
   * ejercicios seguidos podía dejar que la respuesta de la PRIMERA llegara la
   * última y pintara el enunciado equivocado.
   * @param {string} slug
   * @returns {void}
   */
  function cargarEjercicio(slug) {
    slugActual = slug;
    recordarEjercicio('diagrama', slug);
    selectorEjercicios.marcarSeleccionado(slug);
    controladorCargaActual?.abort();
    const controlador = new AbortController();
    controladorCargaActual = controlador;
    seccionEnunciado.mostrarCargando();
    obtenerEjercicioPorSlug(slug, { signal: controlador.signal })
      .then((ejercicio) => {
        if (controlador.signal.aborted) return;
        seccionEnunciado.mostrarEjercicio(ejercicio);
        // TSK-0124: ver la nota de cabecera de este archivo ("De dónde
        // salen ejercicioId/csrfToken"). `ejercicio.id` (UUID real,
        // `EjercicioPublico.id`) solo se conoce aquí, tras resolver esta
        // misma petición ya existente de TSK-0054 -- se reenvía al panel de
        // autocorrección del editor mediante el setter público expuesto por
        // `editor-controller.js`.
        instancia.establecerEjercicioActual(ejercicio.id, {
          // Material pedagógico PÚBLICO (DEC-015) ya cargado para el
          // enunciado: alimenta la ayuda graduada determinista (§10) sin
          // ninguna petición extra. `EjercicioPublico` no puede alojar
          // rúbrica, casos ocultos ni solución de referencia (DEC-016).
          objetivos: ejercicio.contenidoPedagogico.objetivos,
          conceptosPrevios: ejercicio.contenidoPedagogico.conceptosPrevios,
          erroresFrecuentes: ejercicio.contenidoPedagogico.erroresFrecuentes,
        });
      })
      .catch((error) => {
        if (controlador.signal.aborted) return;
        seccionEnunciado.mostrarError(mensajeDeErrorEnunciado(error, TEXTOS_ERROR_ENUNCIADO));
      });
  }

  // Slug inicial: el `?ejercicio=` de la URL tiene prioridad; si no hay
  // (p.ej. al volver de otra pestaña, que navega a `index.html` sin
  // parámetro), se recae en el último ejercicio guardado (petición del
  // usuario: cambiar de pestaña y volver ya no lo resetea).
  const slugDeLaUrl = obtenerSlugDeLaUrl();
  const slugInicial = slugDeLaUrl ?? ejercicioRecordado('diagrama');
  if (slugInicial) {
    cargarEjercicio(slugInicial);
    // Si vino de `localStorage`, la URL todavía no lo refleja: se completa con
    // `replaceState`, igual que hacen las páginas de pseudocódigo y Python.
    if (!slugDeLaUrl) actualizarUrlEjercicio(slugInicial, { reemplazar: true });
  }

  // El botón ATRÁS del navegador tiene que seguir funcionando. Con la
  // navegación completa lo hacía solo; al cambiar de ejercicio con
  // `pushState` hay que atender `popstate` a mano o el usuario vería cambiar
  // la URL sin que cambie el enunciado -- que es peor que la recarga.
  manejadorPopstate = () => {
    const slug = obtenerSlugDeLaUrl() ?? ejercicioRecordado('diagrama');
    if (slug) cargarEjercicio(slug);
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', manejadorPopstate);
  }

  // TSK-0117: carga del selector de ejercicios del bloque 'diagrama'.
  // Fire-and-forget deliberado (no se bloquea el montaje del editor por
  // ella) pero SIEMPRE capturada -- nunca una promesa rechazada sin
  // manejar (criterio de aceptación de la ficha). El `AbortController`
  // propio se cancela en `desmontarPaginaEditor` para no actualizar un
  // selector ya destruido si la página se desmonta antes de que resuelva.
  const controladorEjercicios = new AbortController();
  controladorEjerciciosActual = controladorEjercicios;
  selectorEjercicios.mostrarCargando();
  /**
   * Pide la lista de ejercicios del bloque y la vuelca en el selector.
   *
   * Extraída a función el 2026-08-07 para poder volver a llamarla al
   * COMPLETAR un ejercicio, no solo al montar. Antes la lista se cargaba una
   * sola vez, así que al acertar el panel anunciaba "se desbloquea el
   * siguiente" y el desplegable seguía sin ofrecerlo: había que refrescar la
   * página a mano. Lo encontró el usuario probando la aplicación.
   */
  function recargarListaDeEjercicios() {
    if (controladorEjercicios.signal.aborted) return;
    cargarEjercicios(BLOQUE_EJERCICIOS, { signal: controladorEjercicios.signal })
    .then((items) => {
      if (controladorEjercicios.signal.aborted) return;
      // Se guarda para poder remontar al cambiar de ejercicio sin volver a
      // pedirla: en el plan gratuito, con la API dormida, se nota.
      ejerciciosCargados = items;
      selectorEjercicios.mostrarLista(items);
      // Si el ejercicio que se está trabajando ya está superado, el panel de
      // corrección tiene que nacer bloqueado: no se repite lo que ya está
      // bien. Este dato solo viaja en la LISTA, no en el enunciado.
      if (slugActual) {
        const actual = items.find((item) => item?.ejercicio?.slug === slugActual);
        instancia.establecerEjercicioCompletado(soloSePractica(actual?.estado));
      }
      // Sincroniza el desplegable con el ejercicio que se está trabajando.
      // `marcarSeleccionado` es solo visual: no invoca `onSeleccionar`, que
      // dispararía otra carga.
      //
      // Se usa `slugActual` y NO el slug inicial: la lista de ejercicios y el
      // enunciado se piden a la vez, así que el alumno puede cambiar de
      // ejercicio antes de que la lista termine de llegar. Marcar el inicial
      // dejaría el desplegable señalando uno distinto del que está viendo.
      if (slugActual) {
        selectorEjercicios.marcarSeleccionado(slugActual);
      }
    })
    .catch((error) => {
      if (controladorEjercicios.signal.aborted) return;
      selectorEjercicios.mostrarError(mensajeDeErrorEjercicios(error));
    });
  }

  recargarListaDeEjercicios();

  return instancia;
}

/**
 * Desmonta la página actual (si hay una montada): cancela cualquier carga
 * de ejercicio en curso, destruye la instancia de la feature (que a su vez
 * destruye paleta/canvas/operaciones/conectores y retira sus listeners, ver
 * `editor-controller.js`) y vacía el contenedor. Es seguro llamarla sin que
 * haya nada montado (no hace nada).
 */
export function desmontarPaginaEditor() {
  if (manejadorPopstate) {
    if (typeof window !== 'undefined') {
      window.removeEventListener('popstate', manejadorPopstate);
    }
    manejadorPopstate = null;
  }
  if (controladorCargaActual) {
    controladorCargaActual.abort();
    controladorCargaActual = null;
  }
  if (controladorEjerciciosActual) {
    controladorEjerciciosActual.abort();
    controladorEjerciciosActual = null;
  }
  if (selectorEjerciciosActual) {
    selectorEjerciciosActual.destruir();
    selectorEjerciciosActual = null;
  }
  if (!instanciaActual) return;
  const instancia = instanciaActual;
  // El diálogo de traducción vive en `document.body`, así que no se iría con
  // el árbol de la página: hay que cerrarlo explícitamente.
  botonTraducirActual?.destruir();
  botonTraducirActual = null;
  instanciaActual = null;
  instancia.destruir();
  wrapperActual?.remove();
  wrapperActual = null;
}

/**
 * Navega a esta misma página (el editor de diagramas, servido como
 * `index.html`) con `?ejercicio=<slug>` en la query string, reutilizando el
 * mecanismo de carga de enunciado ya documentado (TSK-0054): esta página ya
 * sabe leer ese parámetro al montarse (`obtenerSlugDeLaUrl`), así que
 * "seleccionar un ejercicio" en el selector y "abrir el editor apuntando a
 * un ejercicio concreto" son, a propósito, el mismo mecanismo -- no hace
 * falta ningún routing/estado adicional. Exportada (y aceptada como
 * dependencia inyectable en `montarPaginaEditor`, ver `opciones.
 * navegarAEjercicio`) por el mismo motivo que `redirigirALogin`: jsdom no
 * implementa una navegación de documento completo (`Error: Not implemented:
 * navigation`), así que la única forma fiable de verificar el destino
 * exacto de la navegación en tests es inyectar un doble de esta función.
 * @param {string} slug
 * @returns {void}
 */
export function navegarAEjercicio(slug) {
  window.location.assign(`index.html?${PARAMETRO_EJERCICIO}=${encodeURIComponent(slug)}`);
}

/**
 * Refleja el ejercicio elegido en la URL SIN recargar la página.
 *
 * La dirección tiene que seguir siendo compartible y marcable: un alumno que
 * copia la URL y se la manda a un compañero debe abrirle el mismo ejercicio.
 * `pushState` además deja entrada en el historial, así que el botón atrás
 * vuelve al ejercicio anterior (ver el manejador de `popstate`).
 * `reemplazar` para la restauración inicial: deja la dirección compartible sin
 * inventar una entrada de historial que el alumno no ha creado. Sin esto, el
 * botón atrás llevaba a `index.html` SIN parámetro, el ejercicio seguía puesto
 * (se recuperaba de `localStorage`) y la URL dejaba de corresponderse con lo
 * que se veía. Lo detectó el usuario probando la aplicación el 2026-08-06.
 * @param {string} slug
 * @param {{ reemplazar?: boolean }} [opciones]
 * @returns {void}
 */
export function actualizarUrlEjercicio(slug, opciones = {}) {
  if (typeof window === 'undefined' || !window.history?.pushState) return;
  const destino = `index.html?${PARAMETRO_EJERCICIO}=${encodeURIComponent(slug)}`;
  if (opciones.reemplazar) window.history.replaceState({}, '', destino);
  else window.history.pushState({}, '', destino);
}

/**
 * Redirige el navegador a `RUTA_LOGIN`. Exportada (y aceptada como
 * dependencia inyectable en `arrancarPaginaEditor`, ver `opciones.
 * redirigirALogin` más abajo) por el mismo motivo que `login-page.js#
 * redirigirTrasLogin`: jsdom no implementa una navegación de documento
 * completo (`Error: Not implemented: navigation`), así que la única forma
 * fiable de verificar el destino exacto de la redirección en tests es
 * inyectar un doble de esta función.
 * @returns {void}
 */
export function redirigirALogin() {
  // Conserva a dónde quería ir el alumno, para volver tras entrar
  // (`utils/retorno-tras-login.js`).
  window.location.assign(construirRutaLogin(RUTA_LOGIN));
}

/**
 * Punto de entrada REAL de esta página (TSK-0085, CAP-IDENTITY-AUTH-FLOW):
 * comprueba la sesión con `cargarSesionActual()` (`state/session-store.js`,
 * TSK-0082) ANTES de montar cualquier cosa del editor. Si no hay sesión, o
 * si la comprobación falla por cualquier otro motivo (ver decisión abajo),
 * redirige a `login.html` sin llamar a `montarPaginaEditor` en absoluto: ni
 * el canvas, ni la paleta, ni el enunciado de ningún ejercicio llegan a
 * construirse antes de la redirección (criterio de aceptación de la
 * ficha).
 *
 * Decisión documentada sobre errores al comprobar la sesión (no solo
 * "sesión ausente" vs. "sesión presente"): `cargarSesionActual()` distingue
 * "no autenticado" (401 -> `null`, sin lanzar) de cualquier otro fallo
 * (red/timeout/5xx -> lanza el `error` original, ver su docstring en
 * `session-store.js`). Esta página trata AMBOS casos igual -- redirige a
 * login -- porque no hay forma segura de mostrar el editor (que sí expone
 * datos: el autoguardado local de un diagrama previo, el enunciado de un
 * ejercicio) cuando no se puede CONFIRMAR positivamente que existe una
 * sesión válida; un error de red no es una prueba de que la sesión exista.
 * Mismo criterio, en espejo, que el logout del top-nav
 * (`pages/nav-session.js#manejarCerrarSesion`) aplica al fallo de
 * `cerrarSesion()`: ante la duda, forzar el paso por login antes que dejar
 * una pantalla potencialmente protegida visible. Ningún error se registra
 * en consola (mismo criterio que `session-store.js`).
 *
 * @param {HTMLElement} [contenedor]
 * @param {{
 *   redirigirALogin?: () => void,
 *   cargarEjerciciosDeBloque?: typeof obtenerEjerciciosDeBloque,
 *   listaInicial?: readonly unknown[] | null,
 *   navegarAEjercicio?: (slug: string) => void,
 * }} [opciones] `redirigirALogin` inyectable en tests (misma razón que
 *   `login-page.js#montarPaginaLogin`). TSK-0117:
 *   `cargarEjerciciosDeBloque`/`navegarAEjercicio` se reenvían tal cual a
 *   `montarPaginaEditor` (mismo motivo de inyectabilidad en tests).
 * @returns {Promise<ReturnType<typeof crearEditorDiagramas> | null>}
 */
export async function arrancarPaginaEditor(contenedor, opciones = {}) {
  const redirigir = opciones.redirigirALogin ?? redirigirALogin;

  let usuarioCargado;
  try {
    usuarioCargado = await cargarSesionActual();
  } catch {
    usuarioCargado = null;
  }

  if (!usuarioCargado) {
    redirigir();
    return null;
  }

  // La identidad del usuario ya no la muestra esta página: la pastilla + logout
  // viven en el top-nav (`shell-nav.js`). Aquí solo se necesita la guarda de
  // sesión (arriba) antes de montar el editor.
  return montarPaginaEditor(contenedor, {
    cargarEjerciciosDeBloque: opciones.cargarEjerciciosDeBloque,
    navegarAEjercicio: opciones.navegarAEjercicio,
  });
}

// --------------------------------------------------------------------------
// Auto-montaje cuando este módulo se carga como
// `<script type="module" src="/src/pages/diagram-editor-page.js">` desde
// `frontend/index.html` (nunca JS inline en el HTML, docs/03-frontend.md
// §6.1). Guardado explícitamente detrás de dos comprobaciones para no
// producir efectos secundarios inesperados al importar este módulo desde
// las suites de test (que construyen su propio `contenedor` y llaman a
// `montarPaginaEditor(contenedor)`/`arrancarPaginaEditor(contenedor, ...)`
// explícitamente, sin usar el anclaje del shell real):
//   1. `import.meta.env.MODE !== 'test'`: Vitest fija `MODE` a `'test'` por
//      defecto (ver `frontend/vite.config.js`, `test.environment`), así que
//      este bloque nunca se ejecuta bajo `vitest run`.
//   2. El propio `montarPaginaEditor()` sin argumento ya no hace nada si no
//      encuentra `#diagram-editor-root` en el documento (ver arriba): una
//      segunda red de seguridad si algún entorno futuro no fijase `MODE`.
//
// TSK-0085: el auto-montaje real llama a `arrancarPaginaEditor()` (con la
// guarda de sesión), NUNCA directamente a `montarPaginaEditor()` -- así el
// único camino de carga real del navegador queda protegido por sesión.
if (typeof document !== 'undefined' && import.meta.env?.MODE !== 'test') {
  const iniciar = () => {
    // Rediseño, Fase 6b: los momentos de recompensa (la consola que
    // teclea, los XP que cuentan). Solo en el navegador de verdad.
    activarMomentos();
    arrancarPaginaEditor();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar, { once: true });
  } else {
    iniciar();
  }
}
